/* =============================================================================
 * Volleybird Darling PDP
 * - IntersectionObserver-driven sticky ATC (no scroll listeners)
 * - Variant switching without full page reload (updates price, URL, ATC label)
 * - Deep-link support via ?variant=
 * - Selection-required behavior: unfilled group flashes instead of submitting
 * - Suppresses sticky bar when drawer/modal open
 * ============================================================================= */
(function () {
  'use strict';

  const section = document.querySelector('[data-pdp-darling]');
  if (!section) return;

  const form         = section.querySelector('[data-pdp-form]');
  const variantInput = section.querySelector('[data-pdp-variant-id]');
  const priceEl      = section.querySelector('[data-pdp-price]');
  const compareEl    = section.querySelector('[data-pdp-compare]');
  const saveEl       = section.querySelector('[data-pdp-save]');
  const priceRowEl   = section.querySelector('[data-pdp-price-row]');
  const atc          = section.querySelector('[data-pdp-atc]');
  const atcPrice     = section.querySelector('[data-pdp-atc-price]');
  const atcLabel     = section.querySelector('[data-pdp-atc-label]');
  const colorValueEl = section.querySelector('[data-pdp-color-value]');
  const slidesEl     = section.querySelector('[data-pdp-slides]');
  const dotsWrap     = section.querySelector('[data-pdp-dots]');
  const stickyBar    = section.querySelector('[data-sticky-atc]');
  const stickyBtn    = section.querySelector('[data-pdp-sticky-btn]');
  const stickyLabel  = section.querySelector('[data-pdp-sticky-label]');
  const stickyPrice  = section.querySelector('[data-pdp-sticky-price]');
  const stickyRecap  = section.querySelector('[data-pdp-sticky-recap]');

  const variantsScript = section.querySelector('[data-pdp-variants]');
  let variants = [];
  try { variants = JSON.parse(variantsScript.textContent); } catch (e) { variants = []; }

  const productUrl = section.dataset.url;

  /* ---------------- money format (safe fallback) ---------------- */
  function formatMoney(cents) {
    // Try Shopify.formatMoney if the theme exposes it
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
      return window.Shopify.formatMoney(cents, fmt);
    }
    const dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ---------------- variant matching ---------------- */
  function getSelectedOptions() {
    const selects = {};
    section.querySelectorAll('[data-pdp-pill] input[type="radio"]:checked').forEach((el) => {
      const pill = el.closest('[data-pdp-pill]');
      selects[pill.dataset.optionPosition] = pill.dataset.optionValue;
    });
    return selects;
  }

  function findMatchingVariant() {
    const selected = getSelectedOptions();
    return variants.find((v) => {
      return (
        (!selected['1'] || v.option1 === selected['1']) &&
        (!selected['2'] || v.option2 === selected['2']) &&
        (!selected['3'] || v.option3 === selected['3'])
      );
    });
  }

  function allOptionsSelected() {
    const groups = section.querySelectorAll('[data-pdp-group]');
    for (const g of groups) {
      if (!g.querySelector('input[type="radio"]:checked')) return false;
    }
    return groups.length > 0;
  }

  /* ---------------- UI updates ---------------- */
  function updateUI(variant) {
    if (!variant) {
      atc.disabled = true;
      atcLabel.textContent = 'Unavailable';
      return;
    }

    variantInput.value = variant.id;
    atc.disabled = !variant.available;

    if (variant.available) {
      const priceStr = formatMoney(variant.price);
      priceEl.textContent = priceStr;
      atcPrice.textContent = priceStr;
      atcLabel.innerHTML = 'Add to cart · <span data-pdp-atc-price>' + priceStr + '</span>';
      if (stickyPrice) stickyPrice.textContent = priceStr;
      if (stickyLabel) stickyLabel.innerHTML = 'Add · <span data-pdp-sticky-price>' + priceStr + '</span>';

      // Compare-at + save %
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const cmpStr = formatMoney(variant.compare_at_price);
        const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        if (compareEl) {
          compareEl.textContent = cmpStr;
          compareEl.hidden = false;
        }
        if (saveEl) {
          saveEl.textContent = 'SAVE ' + pct + '%';
          saveEl.hidden = false;
        }
      } else {
        if (compareEl) compareEl.hidden = true;
        if (saveEl) saveEl.hidden = true;
      }
    } else {
      atcLabel.textContent = 'Sold out';
    }

    // Sticky recap
    if (stickyRecap) {
      const opts = variant.options.filter(Boolean).join(' · ');
      stickyRecap.textContent = opts || variant.options.join(' · ');
    }

    // Selected color label under group label
    if (colorValueEl) {
      const selected = getSelectedOptions();
      const groups = section.querySelectorAll('[data-pdp-group]');
      const colorGroup = Array.from(groups).find((g) => g.querySelector('[data-pdp-color-value]'));
      if (colorGroup) {
        const pos = colorGroup.dataset.pdpGroup;
        colorValueEl.textContent = selected[pos] || '';
      }
    }

    // Update URL (?variant=) without a reload — helps paid traffic pre-select
    if (productUrl && history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.toString());
    }

    // Swap gallery to variant's featured image (if any and different)
    if (variant.featured_media_index !== null && slidesEl) {
      const slide = slidesEl.querySelector('[data-slide-index="' + variant.featured_media_index + '"]');
      if (slide) {
        const offset = slide.offsetLeft - slidesEl.offsetLeft;
        slidesEl.scrollTo({ left: offset, behavior: 'smooth' });
      }
    }
  }

  function markUnavailableCombos() {
    // For each pill, check whether choosing it would produce an available variant
    const selected = getSelectedOptions();
    section.querySelectorAll('[data-pdp-pill]').forEach((pill) => {
      const pos = pill.dataset.optionPosition;
      const val = pill.dataset.optionValue;
      const testSelects = { ...selected, [pos]: val };
      const match = variants.find((v) => {
        return (
          (!testSelects['1'] || v.option1 === testSelects['1']) &&
          (!testSelects['2'] || v.option2 === testSelects['2']) &&
          (!testSelects['3'] || v.option3 === testSelects['3']) &&
          v.available
        );
      });
      if (match) {
        pill.classList.remove('is-unavailable');
      } else {
        pill.classList.add('is-unavailable');
      }
    });
  }

  /* ---------------- pill wiring ---------------- */
  section.querySelectorAll('[data-pdp-pill]').forEach((pill) => {
    const input = pill.querySelector('input[type="radio"]');
    if (!input) return;
    input.addEventListener('change', () => {
      // Update selected state within group
      const group = pill.closest('[data-pdp-group]');
      group.querySelectorAll('[data-pdp-pill]').forEach((p) => p.classList.remove('is-selected'));
      pill.classList.add('is-selected');

      const variant = findMatchingVariant();
      updateUI(variant);
      markUnavailableCombos();
    });
  });

  /* ---------------- form submit: enforce required options ---------------- */
  form.addEventListener('submit', (e) => {
    if (!allOptionsSelected()) {
      e.preventDefault();
      const unfilled = Array.from(section.querySelectorAll('[data-pdp-group]')).find(
        (g) => !g.querySelector('input[type="radio"]:checked')
      );
      if (unfilled) {
        unfilled.scrollIntoView({ behavior: 'smooth', block: 'center' });
        unfilled.querySelectorAll('[data-pdp-pill]').forEach((p) => {
          p.classList.add('is-unfilled-flash');
          setTimeout(() => p.classList.remove('is-unfilled-flash'), 900);
        });
      }
    }
  });

  /* ---------------- sticky bar: IntersectionObserver ---------------- */
  if (stickyBar && atc && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      ([entry]) => {
        const shouldShow = !entry.isIntersecting;
        if (shouldShow) {
          stickyBar.setAttribute('data-visible', '');
          stickyBar.setAttribute('aria-hidden', 'false');
          stickyBar.removeAttribute('inert');
        } else {
          stickyBar.removeAttribute('data-visible');
          stickyBar.setAttribute('aria-hidden', 'true');
          stickyBar.setAttribute('inert', '');
        }
      },
      { threshold: 0, rootMargin: '0px 0px -1px 0px' }
    );
    io.observe(atc);
  }

  // Sticky button submits the real form (or scrolls to unfilled group)
  if (stickyBtn) {
    stickyBtn.addEventListener('click', () => {
      if (!allOptionsSelected()) {
        const unfilled = Array.from(section.querySelectorAll('[data-pdp-group]')).find(
          (g) => !g.querySelector('input[type="radio"]:checked')
        );
        if (unfilled) {
          unfilled.scrollIntoView({ behavior: 'smooth', block: 'center' });
          unfilled.querySelectorAll('[data-pdp-pill]').forEach((p) => {
            p.classList.add('is-unfilled-flash');
            setTimeout(() => p.classList.remove('is-unfilled-flash'), 900);
          });
        }
        return;
      }
      form.requestSubmit();
    });
  }

  // Suppress sticky when cart drawer / modal opens (heuristic: body.overflow-hidden or Dawn's cart-drawer)
  if (stickyBar) {
    const bodyObs = new MutationObserver(() => {
      const drawer = document.querySelector('cart-drawer[open], cart-drawer.active, .cart-drawer.active');
      const modal = document.querySelector('.modal--open, dialog[open]');
      if (drawer || modal || document.body.classList.contains('overflow-hidden-mobile')) {
        stickyBar.setAttribute('data-suppressed', '');
      } else {
        stickyBar.removeAttribute('data-suppressed');
      }
    });
    bodyObs.observe(document.body, { attributes: true, attributeFilter: ['class'], subtree: true, childList: true });
  }

  /* ---------------- gallery: dot pager (native scroll-snap) ---------------- */
  if (slidesEl && dotsWrap) {
    const slides = Array.from(slidesEl.querySelectorAll('[data-slide-index]'));
    const dots = Array.from(dotsWrap.querySelectorAll('[data-dot-index]'));

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.dotIndex, 10);
        const target = slides[idx];
        if (target) {
          const offset = target.offsetLeft - slidesEl.offsetLeft;
          slidesEl.scrollTo({ left: offset, behavior: 'smooth' });
        }
      });
    });

    // Update active dot on scroll
    const slideObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
            const idx = parseInt(entry.target.dataset.slideIndex, 10);
            dots.forEach((d) => {
              const active = parseInt(d.dataset.dotIndex, 10) === idx;
              d.classList.toggle('is-active', active);
              d.setAttribute('aria-selected', active ? 'true' : 'false');
            });
          }
        });
      },
      { root: slidesEl, threshold: [0.55] }
    );
    slides.forEach((s) => slideObs.observe(s));
  }

  /* ---------------- deep link ?variant= ---------------- */
  (function initFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const variantId = params.get('variant');
    if (!variantId) return;
    const v = variants.find((x) => String(x.id) === String(variantId));
    if (!v) return;
    // Set radios to match
    v.options.forEach((val, i) => {
      if (!val) return;
      const pos = i + 1;
      const pill = section.querySelector(
        '[data-pdp-pill][data-option-position="' + pos + '"][data-option-value="' + CSS.escape(val) + '"]'
      );
      if (pill) {
        const input = pill.querySelector('input[type="radio"]');
        if (input && !input.checked) {
          input.checked = true;
          pill.closest('[data-pdp-group]')
            .querySelectorAll('[data-pdp-pill]')
            .forEach((p) => p.classList.remove('is-selected'));
          pill.classList.add('is-selected');
        }
      }
    });
    updateUI(v);
    markUnavailableCombos();
  })();

  // Initial pass to mark unavailable combos with the pre-selected values
  markUnavailableCombos();
})();
